import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { debounceTime } from 'rxjs/operators';
import { hasIn } from 'lodash';
import {
  ByteUnit,
  ChannelType,
  ConsumerPlanV2,
  EligibilityFilterType,
  EligibilityService,
  FormType,
  GetPlanEligibilityParams,
  GetPlanEligibilityResponse,
  GetUserPlansV2Params,
  GetUserPlansV2Response,
  Line,
  PaginationResponse,
  PlanClass,
  Speed,
  TrafficLabelId,
  UserPlanSortOrder,
  UserPlansService,
  UserPlanStatus,
  UserPlanV2,
  UtilsService,
} from 'src/app/core';
import { AppState } from 'src/app/core/reducers';
import { MultiStepFormStep } from 'src/app/shared/components/multi-step-form/multi-step-form-step.class';
import { MultiStepFormComponent } from 'src/app/shared/components/multi-step-form/multi-step-form.component';

@UntilDestroy()
@Component({
  selector: 'app-select-plan-form',
  templateUrl: './select-plan-form.component.html',
  styleUrls: ['./select-plan-form.component.less'],
})
export class SelectPlanFormComponent
  extends MultiStepFormStep
  implements OnInit
{
  @Input() multiStepForm: MultiStepFormComponent;
  @Input() PLAN_LABEL: string;
  stepForm: FormGroup;
  isLoading: boolean;
  MULTI_STEP_FORM_PREFIX: string;
  FormType = FormType;
  byteUnits: string[] = Object.keys(ByteUnit)
    .filter((unit) => unit === ByteUnit.GB || unit === ByteUnit.MB)
    .reverse();
  plans: ConsumerPlanV2[] = [];
  filteredPlans: ConsumerPlanV2[] = [];
  preProvisioningPlan: ConsumerPlanV2;
  currentUserPlan: UserPlanV2;
  lastPage: number;
  pagerResponse: PaginationResponse = {
    lastPage: 0,
    totalItemCount: 0,
  };
  customPlansParam: GetPlanEligibilityParams = {
    channels: [ChannelType.CHANNEL_PORTAL],
    pager: {
      page: 1,
      perPage: 9,
    },
  };
  @Output() onStepFormChange: EventEmitter<any> = new EventEmitter<any>();

  private _line: Line;
  private _groupId: string;

  get line(): Line {
    return this._line;
  }

  @Input() set line(line: Line) {
    this._line = line;

    if (line) {
      this.customPlansParam = {
        ...this.customPlansParam,
        lineId: line.lineId,
      };
      this._getPlans();
    }
  }

  get groupId(): string {
    return this._groupId;
  }

  @Input() set groupId(groupId: string) {
    this._groupId = groupId;

    if (groupId) {
      this.customPlansParam = {
        ...this.customPlansParam,
        groupId: groupId,
      };
      this._getPlans();
    }
  }

  get isGrantPlanFormType(): boolean {
    return (
      this.multiStepForm.formType === FormType.GRANT_PLAN ||
      this.multiStepForm.formType === FormType.PRE_PROVISION_SIM
    );
  }

  constructor(
    protected fb: FormBuilder,
    protected store: Store<AppState>,
    private userPlansService: UserPlansService,
    private eligibilityService: EligibilityService,
    private utilsService: UtilsService
  ) {
    super(fb, store);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this._buildForm();
    this._planIdValueChanges();
    this._formValueChanges();
  }

  onSearchPlan(query: string): void {
    const q = query.toLowerCase().trim();
    this.filteredPlans =
      q.length > 0
        ? this.filteredPlans.filter((plan) =>
            plan.planDisplayName.toLowerCase().includes(q)
          )
        : this.plans;
  }

  onPaginationIndexChange(page: number): void {
    this.customPlansParam = {
      ...this.customPlansParam,
      pager: {
        ...this.customPlansParam.pager,
        page,
      },
    };
    this._getPlans();
  }

  private _getPlans(): void {
    if (this.isGrantPlanFormType) {
      this.customPlansParam = {
        ...this.customPlansParam,
        eligibilities: [EligibilityFilterType.ELIGIBLE],
      };
    } else if (this.multiStepForm.formType === FormType.PLAN_SUBSCRIPTION) {
      this.customPlansParam = {
        ...this.customPlansParam,
        planClasses: [PlanClass.BASE],
      };
    }

    this._getPlanEligibility();

    if (this.isGrantPlanFormType) {
      this._getCurrentPlanTransaction();
    }
  }

  private _getPlanEligibility(): void {
    this.isLoading = true;
    this.eligibilityService
      .getPlanEligibility(this.customPlansParam)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: GetPlanEligibilityResponse) => {
          this.plans = this.filteredPlans = response.plans;
          this.preProvisioningPlan = response.preProvisionPlan;
          this.lastPage = response.pagerResponse?.lastPage
            ? this.customPlansParam?.pager?.page
            : this.customPlansParam?.pager?.page + 1;
          this.isLoading = false;
        },
        () => {
          this.plans = this.filteredPlans = [];
          this.preProvisioningPlan = null;
          this.lastPage = 1;
          this.isLoading = false;
        }
      );
  }

  private _getCurrentPlanTransaction() {
    let getUserPlansV2Param: GetUserPlansV2Params = {
      pager: {
        page: 1,
        perPage: 1,
      },
      statuses: [UserPlanStatus.USERPLAN_ACTIVE],
      userPlanSortOrder: UserPlanSortOrder.UPSO_ON_STATUS,
    };
    if (this.line) {
      getUserPlansV2Param.lineId = this.line.lineId;
    } else if (this.groupId) {
      getUserPlansV2Param.groupId = this.groupId;
    }
    this.userPlansService
      .getUserPlansV2(getUserPlansV2Param)
      .pipe(untilDestroyed(this))
      .subscribe((response: GetUserPlansV2Response) => {
        if (response.userPlans?.length) {
          this.currentUserPlan = response.userPlans[0];
        }
      });
  }

  private _buildForm(): void {
    const selectedPlanId = this.multiStepForm.entity.plan?.parentPlanId
      ? this.multiStepForm.entity.plan?.parentPlanId
      : this.multiStepForm.entity.plan?.planId;

    // Convert plan's values for UI on edit mode
    const plan = this._convertPlanValues(this.multiStepForm.entity.plan);

    this.stepForm = this.fb.group({
      plan: this.fb.control(
        this.multiStepForm.entity.plan,
        Validators.required
      ),
      temp_plan: this.fb.group({
        ...plan,
        planId: this.fb.control(selectedPlanId, Validators.required),
      }),
    });
  }

  private _convertPlanValues(plan: any): any {
    if (plan?.isPersonalPlan) {
      const duration = +plan.duration;
      const voice = +plan.voice?.initial;
      const finalPrice = +plan.finalPrice;

      let capacity = 0;
      let convertedCapacity = null;
      if (hasIn(plan, 'capacity')) {
        capacity = +plan.capacity;
      } else if (
        hasIn(plan, 'trafficLabels') &&
        plan.trafficLabels.length > 0
      ) {
        const general = plan.trafficLabels.find(
          (trafficLabel) =>
            trafficLabel.trafficLabelId === TrafficLabelId.GENERAL &&
            trafficLabel.speed === Speed.SPEED_MAX
        );
        if (general) capacity = +general.capacity;
      }
      if (capacity > 0) {
        convertedCapacity = this.utilsService.convertKibBasedOnByteUnits(
          capacity,
          this.byteUnits
        );
      }

      return {
        ...plan,
        duration:
          duration > 0 ? this.utilsService.convertSecToDay(duration) : null,
        capacity: capacity > 0 ? convertedCapacity?.value : null,
        temp_capacityUnit: convertedCapacity?.unit || ByteUnit.GB,
        voice: {
          ...plan.voice,
          initial: voice > 0 ? this.utilsService.convertSecToMin(voice) : null,
        },
        finalPrice:
          finalPrice > 0
            ? this.utilsService.convertToDollars(finalPrice)
            : null,
      };
    }

    return {};
  }

  private _planIdValueChanges(): void {
    this.stepForm
      .get('temp_plan.planId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((planId: string) => {
        const plan = this.plans.find((plan) => plan.planId === planId);
        this.stepForm.get('plan').setValue(plan);
      });
  }

  private _formValueChanges(): void {
    this.stepForm.valueChanges
      .pipe(untilDestroyed(this), debounceTime(200))
      .subscribe((value) => {
        this.onStepFormChange.emit(value);
      });
  }
}
